import {Component, Input, OnInit} from '@angular/core';
import {FormControl, FormGroup, Validators} from '@angular/forms';
import {StepperService} from '../../../stepper/stepper.service';
import {StepComponent, StepData} from '../../../stepper/step/step.component';
import {Step} from '../../../stepper/step/step';
import {MydocmaPrompt, PromptDialogComponent} from '@mydocma/component-library/src/lib/prompt-dialog';
import {MatDialog} from '@angular/material/dialog';
import {TranslateService} from '@ngx-translate/core';

interface SheetRowItem {
    columnName: string;
    foundedValue: string;
    label: string;
}

@Component({
    selector: 'mydocma-export-step-data-mapping',
    templateUrl: './export-step-data-mapping.component.html',
    styleUrls: ['./export-step-data-mapping.component.scss']
})
export class ExportStepDataMappingComponent implements StepComponent, OnInit {

    @Input()
    public step: Step;

    public fieldsToExport: SheetRowItem[] = [];

    public formGroup = new FormGroup({});

    public fieldsToExportMap: Map<string, boolean> = new Map();

    constructor(
        private readonly stepperService: StepperService,
        private readonly matDialog: MatDialog,
        private readonly translateService: TranslateService
    ) {
    }

    public ngOnInit() {
        setTimeout(() => {
            this.setData();
        });
    }

    public enter() {
        if (this.step.completed) {
            return;
        }

        this.stepperService.setStepData(this.step, {
            error: null,
            data: this.step.data,
            valid: true
        });

        this.formGroup = new FormGroup({});
        this.fieldsToExport = [];

        if (!this.step.data || !this.step.data.length) {
            return;
        }

        for (const [key, value] of Object.entries(this.step.data[0])) {
            this.fieldsToExportMap.set(key, true);

            const validators = [];
            validators.push(Validators.required);

            if (!this.formGroup.get(key)) {
                this.formGroup.addControl(key, new FormControl(key, validators));
            }
            this.formGroup.controls[key].setValue(key);

            this.fieldsToExport.push({
                columnName: key,
                foundedValue: key,
                label: key
            });
        }
    }

    public async validate(): Promise<StepData> {
        this.setData();

        if (!this.formGroup.valid) {
            await this.promptDiscardChanges();
        } else {
            return {
                error: null,
                data: this.step.data,
                valid: true
            };
        }
    }

    public setData() {
        const formValue = this.formGroup.value;

        const headers = [];
        for (const [key, value] of Object.entries(formValue)) {
            if (!value) {
                delete formValue[key];
            }
            if (!this.fieldsToExportMap.get(key)) {
                delete formValue[key];
            }
            headers.push(key);
        }

        const exportData = [];
        this.step.data.forEach(item => {
            const row = [];
            headers.forEach(headerName => {
                row.push(item[headerName]);
            });
            exportData.push(row);
        });

        const exportDataHeaders = [];
        headers.forEach(headerName => {
            exportDataHeaders.push(this.formGroup.get(headerName).value);
        });

        const resultStep = this.stepperService.getStepByIndex(1);
        this.stepperService.setStepData(resultStep, {
            error: null,
            data: {
                exportData,
                exportDataHeaders
            },
            valid: resultStep.completed
        });
    }

    public async nextStep() {
        if (this.formGroup.valid) {
            await this.stepperService.nextStep();

            return;
        }

        await this.promptDiscardChanges();
    }

    public onFieldChange() {
        this.setData();
    }

    public onCheckboxChange(event, key) {
        this.fieldsToExportMap.set(key, event.checked);
    }

    private async promptDiscardChanges(): Promise<boolean> {
        const dialogData: MydocmaPrompt = {
            title: await this.translateService.get('IE.step-file-data-mapping.dialog-mapping.errorTiTle').toPromise(),
            content: await this.translateService.get('IE.step-file-data-mapping.dialog-mapping.errorText').toPromise(),
            yes: await this.translateService.get('IE.step-file-data-mapping.dialog-mapping.close').toPromise(),
            no: null,
        };
        const dialogRef = this.matDialog.open(PromptDialogComponent, {data: dialogData});

        return await dialogRef.afterClosed().toPromise();
    }
}
